import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { alpha } from "../../theme/tokens";
import ExcelResultTable from "./ExcelResultTable";
import CalculationWarning from "./CalculationWarning";
import PipeSpecificationsTable from "./PipeSpecificationsTable";
import VacuumBreakerCalculation from "./VacuumBreakerCalculation";
import { useTheme } from "@mui/material/styles";
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Backdrop,
    CircularProgress,
    Tabs,
    Tab,
    Collapse,
    Tooltip,
    IconButton,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CalculateIcon from "@mui/icons-material/Calculate";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { CALCULATED_NOZZLES } from "./budgetFormConfig";
import { formatNumber } from "../../services/helpers/Helpers";

const ERROR_VARIABLE_MAP = {
    W22: "Vapor Desgasificação",
    W24: "Vapor Aquecimento",
    W27: "Vapor Permutador Calor",
    W30: "Entrada Vapor Flash",
    M21: "Alimentação Caldeiras",
    M26: "Condensados",
    M29: "Água Make-up",
    M32: "Retorno Bombas",
};


const PipeDimensionCalculation = ({ data, loading, error }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";
    const p = data?.pipeDimensionResult || {};
    const warnings = data?.warnings || [];
    const [activeView, setActiveView] = useState(0);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [warningsOpen, setWarningsOpen] = useState(false);
    const warningsRef = useRef(null);

    useEffect(() => {
        setLoadingDetail(true);
        const timer = setTimeout(() => setLoadingDetail(false), 400);
        return () => clearTimeout(timer);
    }, [activeView]);

    const waterRows = [
        {
            label: "Alimentação Caldeiras",
            flow: `${p.degassingCapacity}`,
            speed: p.speedI21,
            interiorDiameter: p.interiorDiameter?.toFixed(0),
            specifiedDiameter: p.specifiedDiameter,
            flowUnit: "m³/h",
        },
        {
            label: "Condensados",
            flow: p.condensateVolumetricFlow?.toFixed(1),
            speed: p.speedI26,
            interiorDiameter: p.condensateInteriorDiameter?.toFixed(0),
            specifiedDiameter: p.condensateSpecifiedDiameter,
            flowUnit: "m³/h",
        },
        {
            label: "Água Make-up",
            flow: p.makeUpWaterVolumetricFlow?.toFixed(1),
            speed: p.speedI29,
            interiorDiameter: p.makeUpInteriorDiameter?.toFixed(0),
            specifiedDiameter: p.makeUpSpecifiedDiameter,
            flowUnit: "m³/h",
        },
        {
            label: "Retorno Bombas",
            flow: p.pumpReturnFlowRate?.toFixed(1),
            speed: p.speedI32,
            interiorDiameter: p.pumpReturnInteriorDiameter?.toFixed(0),
            specifiedDiameter: p.pumpReturnSpecifiedDiameter,
            flowUnit: "m³/h",
        },
    ];

    const steamRows = [
        {
            label: "Vapor Desgasificação",
            flow: p.specificThermalEnergyQ22?.toFixed(1),
            speed: p.speedR22,
            pressure: p.availableSteamPressureS22?.toFixed(2),
            temperature: p.deaeratorSteamTemperature?.toFixed(1),
            density: p.saturatedVapourDensity?.toFixed(2),
            interiorDiameter: p.vapourInteriorDiameter?.toFixed(0),
            specifiedDiameter: p.vapourSpecifiedDiameter,
        },
        {
            label: "Vapor Aquecimento",
            flow: p.steamMassFlowRateQ24?.toFixed(1),
            speed: p.speedR24,
            pressure: p.vapourHeatingPressure?.toFixed(2),
            temperature: p.vapourHeatingTemperature?.toFixed(1),
            density: p.vapourHeatingDensity?.toFixed(2),
            interiorDiameter: p.vapourHeatingInteriorDiameter?.toFixed(0),
            specifiedDiameter: p.vapourHeatingSpecifiedDiameter,
        }
    ];

    const isAutoCalculated = (designation) => {
        return CALCULATED_NOZZLES.includes(designation);
    };


    const displayCellValue = (value) => {
        if (value == null || value === "") return "—";
        if (/^-?\d+\.?\d*$/.test(String(value).trim())) {
            return formatNumber(value);
        }
        return String(value);
    };

    const renderTable = (title, headers, rows, keyMap) => (
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
                variant="subtitle1"
                sx={{
                    fontWeight: 700,
                    mb: 1,
                    color: "primary.main",
                    fontSize: "0.9rem",
                }}
            >
                {title}
            </Typography>
            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                    borderRadius: 2,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                }}
            >
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: alpha.primary30 }}>
                            {headers.map((h, i) => (
                                <TableCell
                                    key={i}
                                    sx={{
                                        fontWeight: 700,
                                        color: "var(--text-primary)",
                                        py: 0.75,
                                        px: 1.5,
                                        whiteSpace: "nowrap",
                                        textAlign: i === 0 ? "left" : "center",
                                    }}
                                >
                                    {h}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, idx) => {
                            const autoCalculated = isAutoCalculated(row.designation || row.label);
                            return (
                                <TableRow
                                    key={idx}
                                    sx={{
                                        "&:nth-of-type(odd)": { bgcolor: "rgba(0,0,0,0.02)" },
                                    }}
                                >
                                    {keyMap.map((key, colIdx) => {
                                        const isDnColumn = key === "dn";
                                        return (
                                            <TableCell
                                                key={colIdx}
                                                sx={{
                                                    py: 0.4,
                                                    px: 1.5,
                                                    fontSize: "0.85rem",
                                                    color: isDnColumn && autoCalculated ? "primary.main" : (row[key] ? "var(--text-primary)" : "var(--text-secondary)"),
                                                    fontWeight: 500,
                                                    textAlign: colIdx === 0 ? "left" : "center",
                                                }}
                                            >
                                                {isDnColumn ? (
                                                    <Box sx={{ position: "relative", width: "100%", textAlign: "center" }}>
                                                        {displayCellValue(row[key])}
                                                        {autoCalculated && row[key] && (
                                                            <CalculateIcon
                                                                sx={{
                                                                    position: "absolute",
                                                                    right: 4,
                                                                    top: "50%",
                                                                    transform: "translateY(-50%)",
                                                                    fontSize: "0.75rem",
                                                                    color: "primary.main",
                                                                    opacity: 0.7,
                                                                }}
                                                                titleAccess="Calculado automaticamente"
                                                            />
                                                        )}
                                                    </Box>
                                                ) : (
                                                    displayCellValue(row[key])
                                                )}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    return (
        <Box sx={{ width: "100%", position: "relative", mb: 7 }}>
            <Box sx={{ position: "relative" }}>
                <Tabs
                    value={activeView}
                    onChange={(_, v) => setActiveView(v)}
                    textColor="primary"
                    indicatorColor="primary"
                    sx={{
                        mb: 3,
                        "& .MuiTabs-flexContainer": { justifyContent: "center", gap: 1 },
                        "& .MuiTab-root": {
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: "0.9rem",
                            borderRadius: "8px 8px 0 0",
                            color: "var(--text-secondary)",
                            "&.Mui-selected": { color: "primary.main" },
                        },
                        "& .MuiTabs-indicator": { backgroundColor: "primary.main", height: 3 },
                    }}
                >
                    <Tab label={t("budgetForm.pipeDimension.tabPipes")} />
                    <Tab label={t("budgetForm.pipeDimension.tabHeating")} />
                    <Tab label="Vacuum Breaker" />
                </Tabs>
                {warnings.length > 0 && (
                    <Tooltip title="Ver inconsistências de cálculo" placement="top">
                        <IconButton
                            size="small"
                            onClick={() => {
                                setWarningsOpen(true);
                                warningsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                            }}
                            sx={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-100%)" }}
                        >
                            <ErrorOutlineIcon sx={{ color: "#d32f2f", fontSize: 22 }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {error ? (
                <CalculationWarning error={error} />
            ) : loadingDetail || loading ? (
                <Backdrop
                    open={true}
                    sx={{
                        position: "absolute",
                        inset: 0,
                        backgroundColor: isDarkMode ? "rgba(0, 0, 0, 0.7)" : "rgba(255, 255, 255, 0.85)",
                        color: "var(--color-primary)",
                        zIndex: 1,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <CircularProgress sx={{ color: "primary.main" }} size={40} />
                </Backdrop>
            ) : (
                <>
                    {activeView === 1 ? (
                        <Paper sx={{ p: 2, minHeight: 400 }}>
                            <Typography variant="subtitle1" fontWeight={600} mb={2} sx={{ textAlign: "center", color: "var(--text-primary)" }}>
                                {t("budgetForm.pipeDimension.tabHeating")}
                            </Typography>
                            <ExcelResultTable tableType="WARM_UP" data={data} />
                        </Paper>
                    ) : activeView === 2 ? (
                        <VacuumBreakerCalculation data={data?.vacuumBreakCalculationResult} />
                    ) : (
                        <>
                            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3, mb: 4 }}>
                                {renderTable(
                                    "Tubagens de Água",
                                    ["Designação", "Caudal [m³/h]", "Vel. [m/s]", "Ø Int. [mm]", "Ø Adotado DN"],
                                    waterRows,
                                    ["label", "flow", "speed", "interiorDiameter", "specifiedDiameter"]
                                )}
                                {renderTable(
                                    "Tubagens de Vapor",
                                    ["Designação", "Caudal [kg/h]", "Vel. [m/s]", "Pressão [bar]", "Ø Int. [mm]", "Ø Adotado DN"],
                                    steamRows,
                                    ["label", "flow", "speed", "pressure", "interiorDiameter", "specifiedDiameter"]
                                )}
                            </Box>

                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 700,
                                    color: "var(--text-secondary)",
                                    fontSize: "0.85rem",
                                    textTransform: "uppercase",
                                    letterSpacing: "1px",
                                    mb: 2,
                                    mt: 5,
                                    textAlign: "center",
                                }}
                            >
                                Lista de Tubuladuras
                            </Typography>

                            {data?.nozzleList && (
                                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
                                    {renderTable(
                                        "Tubuladuras Tanque",
                                        ["Designação", "Qt.", "DN", "PN"],
                                        data.nozzleList.tankNozzles || [],
                                        ["designation", "qty", "dn", "pn"]
                                    )}
                                    {renderTable(
                                        "Tubuladuras Torre",
                                        ["Designação", "Qt.", "DN", "PN"],
                                        data.nozzleList.towerNozzles || [],
                                        ["designation", "qty", "dn", "pn"]
                                    )}
                                </Box>
                            )}

                            {warnings.length > 0 && (
                                <Box ref={warningsRef} sx={{ mt: 4 }}>
                                    <Box
                                        onClick={() => setWarningsOpen(o => !o)}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.5,
                                            p: 2,
                                            mb: warningsOpen ? 2 : 0,
                                            borderRadius: warningsOpen ? "8px 8px 0 0" : 2,
                                            border: "1px solid #d32f2f",
                                            backgroundColor: "rgba(211, 47, 47, 0.06)",
                                            cursor: "pointer",
                                            userSelect: "none",
                                        }}
                                    >
                                        <ErrorOutlineIcon sx={{ fontSize: 22, color: "#d32f2f", flexShrink: 0 }} />
                                        <Typography variant="body2" sx={{ color: "#d32f2f", fontWeight: 700, fontSize: "0.875rem", flex: 1 }}>
                                            Não foi possível calcular os seguintes dados — o orçamento pode conter inconsistências.
                                        </Typography>
                                        {warningsOpen
                                            ? <KeyboardArrowUpIcon sx={{ color: "#d32f2f", fontSize: 20 }} />
                                            : <KeyboardArrowDownIcon sx={{ color: "#d32f2f", fontSize: 20 }} />
                                        }
                                    </Box>
                                    <Collapse in={warningsOpen}>
                                        <Box sx={{ border: "1px solid #d32f2f", borderTop: "none", borderRadius: "0 0 8px 8px", p: 2 }}>
                                            {warnings.map((w, i) => {
                                                const match = w.match(/Variable\[(\w+)\]/);
                                                const code = match?.[1];
                                                const label = ERROR_VARIABLE_MAP[code] || code;
                                                const detail = w.replace(/Variable\[\w+\]:\s*/, "");
                                                return (
                                                    <Box
                                                        key={i}
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "flex-start",
                                                            gap: 1.5,
                                                            p: 1.5,
                                                            mb: 1,
                                                            borderRadius: 2,
                                                            border: "1px solid #ffc107",
                                                            backgroundColor: "rgba(255, 193, 7, 0.06)",
                                                        }}
                                                    >
                                                        <WarningAmberIcon sx={{ fontSize: 18, color: "#ff9800", mt: "2px", flexShrink: 0 }} />
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 700, color: "var(--text-primary)", fontSize: "0.82rem" }}>
                                                                {label}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: "var(--text-secondary)", display: "block", mt: 0.25 }}>
                                                                {detail}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                            <PipeSpecificationsTable />
                                        </Box>
                                    </Collapse>
                                </Box>
                            )}
                        </>
                    )}
                </>
            )}
        </Box>
    );
};

export default PipeDimensionCalculation;
